import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RejectModal } from '../components/RejectModal.js';
import { SearchProfileFilter } from '../components/SearchProfileFilter.js';
import { ShareModal } from '../components/ShareModal.js';
import { ScoreBadge } from '../components/ScoreBadge.js';
import {
  buildMapsUrl,
  listObjects,
  partnerApproveObject,
  profileLabel,
  rejectObject,
  shortlistObject,
  type ObjectView,
  type RejectCategory,
} from '../lib/api.js';


function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}
function fmtArea(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} m²`;
}
function fmtSince(ts: number | null): string {
  if (ts === null) return '';
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'heute';
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  return `vor ${Math.floor(days / 7)} Wo`;
}

/**
 * Phase 4 — Partner-Check: Objekte die du mit "Hot" weitergeschoben hast und die auf
 * die Bewertung deines Partners warten. Aktionen: Partner Ja → kontaktieren (Phase 5),
 * Partner Nein → ablehnen mit Begründung (source=partner), nochmal per WhatsApp teilen.
 */
export function PartnerCheck(): JSX.Element {
  const [searchProfileId, setSearchProfileId] = useState<number | undefined>(undefined);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [rejectFor, setRejectFor] = useState<number | null>(null);
  const [rejectCategory, setRejectCategory] = useState<RejectCategory | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [shareModal, setShareModal] = useState<{ text: string; title: string; url?: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['partner-check', searchProfileId],
    queryFn: () =>
      listObjects({
        searchProfileId,
        status: 'partner_review',
        minScore: 0,
        limit: 200,
      }),
    refetchInterval: 60_000,
  });

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['partner-check'] });
    qc.invalidateQueries({ queryKey: ['nav-badge-partner'] });
    qc.invalidateQueries({ queryKey: ['objects'] });
  };

  const approveMut = useMutation({
    mutationFn: (id: number) => partnerApproveObject(id),
    onSuccess: () => {
      invalidate();
      notifications.show({ message: '✓ Partner OK → in Kontakt-Liste', color: 'green' });
    },
    onError: (err) => notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, category, note }: { id: number; category: RejectCategory; note: string }) =>
      rejectObject(id, category, note || undefined, 'partner'),
    onSuccess: () => {
      invalidate();
      setRejectFor(null);
      setRejectCategory(null);
      setRejectNote('');
      notifications.show({ message: '✗ Vom Partner abgelehnt', color: 'red' });
    },
    onError: (err) => notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const shareMut = useMutation({
    mutationFn: (obj: ObjectView) => shortlistObject(obj.id),
    onSuccess: (res, obj) =>
      setShareModal({ text: res.whatsappText, title: obj.listing?.title ?? 'Inserat', url: obj.listing?.url ?? undefined }),
    onError: (err) => notifications.show({ message: `Fehler: ${String(err)}`, color: 'red' }),
  });

  const objs = data?.objects ?? [];

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={1}>🤝 Partner-Check</Title>
        <Text c="dimmed" size="sm">
          Phase 4: Du hast „Hot" gewählt — diese {objs.length} warten auf die Bewertung deines Partners.
        </Text>
      </Stack>

      <SearchProfileFilter value={searchProfileId} onChange={setSearchProfileId} />

      {isLoading && <Center py="xl"><Loader /></Center>}
      {error && <Card><Text c="red.7">Fehler: {String(error)}</Text></Card>}

      {data && objs.length === 0 && (
        <Card padding="xl">
          <Center py="lg">
            <Stack align="center" gap="md">
              <Text size="2.5rem">🤝</Text>
              <Text size="lg" fw={600} ta="center">Nichts zum Bewerten</Text>
              <Text c="dimmed" size="sm" ta="center" maw={300}>
                Wisch im Swipe-Tab nach rechts (Hot), um Treffer hierher zur Partner-Bewertung zu schieben.
              </Text>
              <Button component={Link} to="/" variant="light" mt="sm">Zum Swipe →</Button>
            </Stack>
          </Center>
        </Card>
      )}

      <Stack gap="md">
        {objs.map((o) => (
          <Card key={o.id} padding="lg" style={{ borderLeft: '4px solid #d6336c' }}>
            <Stack gap="md">
              <Group align="flex-start" wrap="nowrap" gap="md">
                <ScoreBadge score={o.score?.effectiveTotal} size="md" killed={false} />
                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={6} wrap="wrap">
                    <Badge size="sm" color={o.assignedProfile === 'B_buero' ? 'cyan' : 'violet'} variant="light" tt="none">
                      {profileLabel(o.assignedProfile)}
                    </Badge>
                    <Badge size="sm" color="pink" variant="filled" tt="none">🤝 wartet auf Partner</Badge>
                    {o.statusChangedAt && <Text size="xs" c="dimmed">{fmtSince(o.statusChangedAt)}</Text>}
                  </Group>
                  <Text fw={700} size="md" lineClamp={2} style={{ cursor: 'pointer' }} onClick={() => navigate(`/objects/${o.id}`)}>
                    {o.listing?.title ?? '—'}
                  </Text>
                  <Text size="sm" c="dimmed">{o.address ?? '—'}</Text>
                  <Group gap="md" mt={4} wrap="wrap">
                    <Fact label="Fläche" value={fmtArea(o.latestSnapshot?.areaSqm)} />
                    <Fact label="Miete" value={fmtPrice(o.latestSnapshot?.price)} />
                    <Fact label="Pendel" value={o.bestCommuteMin !== null ? `${Math.round(o.bestCommuteMin)} min` : '—'} />
                  </Group>
                </Stack>
              </Group>

              <Group gap="xs" wrap="wrap">
                {o.listing?.url && (
                  <Button component="a" href={o.listing.url} target="_blank" rel="noreferrer" size="xs" variant="light" color="indigo">🌐 Inserat</Button>
                )}
                <Button component="a" href={buildMapsUrl(o)} target="_blank" rel="noreferrer" size="xs" variant="light" color="indigo">🗺 Maps</Button>
                <Button size="xs" variant="subtle" onClick={() => shareMut.mutate(o)} loading={shareMut.isPending}>📲 Nochmal an Partner</Button>
                <Button component={Link} to={`/objects/${o.id}`} size="xs" variant="subtle">📊 Details</Button>
              </Group>

              <Group gap="xs" grow>
                <Button color="green" radius="md" leftSection="✓" onClick={() => approveMut.mutate(o.id)} disabled={approveMut.isPending}>
                  Partner sagt JA → Kontakt
                </Button>
                <Button color="red" variant="light" radius="md" leftSection="✗" onClick={() => setRejectFor(o.id)}>
                  Partner sagt nein
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>

      <RejectModal
        opened={rejectFor !== null}
        onClose={() => { setRejectFor(null); setRejectCategory(null); setRejectNote(''); }}
        note={rejectNote}
        onNoteChange={setRejectNote}
        onPick={(category) => { if (rejectFor) rejectMut.mutate({ id: rejectFor, category, note: rejectNote }); }}
        pending={rejectMut.isPending}
      />
      <ShareModal
        opened={shareModal !== null}
        onClose={() => setShareModal(null)}
        text={shareModal?.text ?? ''}
        title={shareModal?.title ?? ''}
        listingUrl={shareModal?.url}
      />
    </Stack>
  );
}

function Fact({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.04em', fontSize: '0.65rem' }}>{label}</Text>
      <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
    </Stack>
  );
}
